import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition/PageTransition";
import { useSelector, useDispatch } from "react-redux";
import { selectUserAppSettings } from "../../modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import {
  selectActiveApp,
  selectFocusMode,
} from "@/app/globalSlices/appStripSlice";
import { SidebarProvider } from "@/components/ui/sidebar";
import WideSidebar from "../WideSidebar/WideSidebar";
import TopNavBar from "../TopNavBar/TopNavBar";
import MainContent from "../MainContent/MainContent";
import AppLegacyBanner from "@/components/AppLegacyBanner/AppLegacyBanner";
import AnnouncementBanner from "@/components/AnnouncementBanner/AnnouncementBanner";
import SearchModal from "@/modules/hub/features/Search/components/SearchModal/SearchModal";
import { openSearchModal } from "@/modules/hub/features/Search/slices/searchSlice";
import { AppStrip, appStripItems } from "@/components/AppStrip";
import { useActiveAppFromRoute } from "@/hooks/useActiveAppFromRoute";
import { useIsMobile } from "@/hooks/use-mobile";

interface MainLayoutProps {
  section: string | null;
}

const MainLayout: React.FC<MainLayoutProps> = () => {
  const dispatch = useDispatch();
  const [sidebarOpenWide, setSidebarOpenWide] = useState(true);
  const userAppSettings = useSelector(selectUserAppSettings);
  const activeApp = useSelector(selectActiveApp);
  // A course or learning path is open: only the content and the top bar.
  const focusMode = useSelector(selectFocusMode);
  // Under 768px the rail would take a fifth of the screen; the menu sheet
  // carries an app grid instead (see MobileAppGrid).
  const isMobile = useIsMobile();
  const isAccountSetupIncomplete = userAppSettings && !userAppSettings.roleName;

  // Keep AppStrip active state in sync with the current route
  useActiveAppFromRoute();

  // Check if current app is standalone (no sidebar content)
  const isStandaloneApp =
    activeApp &&
    appStripItems.find((item) => item.id === activeApp)?.standalone;

  // Every time the active app changes, the module sub-menu opens so members
  // always see where they are. Standalone apps have no sub-menu, so it closes.
  // The hamburger still toggles it within an app.
  useEffect(() => {
    setSidebarOpenWide(!isStandaloneApp);
  }, [activeApp, isStandaloneApp]);

  // Global keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        dispatch(openSearchModal());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch]);

  const handleSidebarToggle = () => {
    setSidebarOpenWide((prev) => !prev);
  };

  // Re-clicking the current app (activeApp unchanged) still re-opens it.
  const handleAppStripClick = (appId: string) => {
    const clickedApp = appStripItems.find((item) => item.id === appId);
    if (!clickedApp?.standalone) {
      setSidebarOpenWide(true);
    }
  };

  return (
    <>
      {/* h-dvh, not h-screen: on phones 100vh is taller than the visible viewport while the browser toolbar shows, so the document itself could scroll and push the top bar off screen. */}
      <div className="relative flex h-dvh w-full overflow-hidden bg-background text-foreground">
        {/* Main content area with sidebar */}
        <SidebarProvider
          onOpenChange={setSidebarOpenWide}
          open={sidebarOpenWide}
        >
          {/* App Strip - desktop only, z-[60] to stay above Sheet overlay, pointer-events-auto to remain clickable when modal Sheet sets pointer-events:none on body */}
          {!focusMode && !isMobile && (
            <div className="flex z-[60] pointer-events-auto">
              <AppStrip onAppClick={handleAppStripClick} />
            </div>
          )}

          {!focusMode && <WideSidebar />}
          <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
            {/* <AnnouncementBanner /> */}
            <TopNavBar onSidebarToggle={handleSidebarToggle} />
            <MainContent>
              {isAccountSetupIncomplete ? (
                <div> IncompleteAccountSetup goes here</div>
              ) : (
                <PageTransition>
                  <Outlet />
                </PageTransition>
              )}
            </MainContent>
          </div>
        </SidebarProvider>
      </div>
      {/* Global Search Modal */}
      <SearchModal />
    </>
  );
};

export default MainLayout;
